import { useMemo, useState } from 'react';

import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import type { CreditRow } from '@/hooks/useCredits';
import { useCreateCreditRequest, type RequestOpenInvoice } from '@/hooks/useCreditRequests';

/**
 * "Tell us what to do" with one unapplied payment or credit memo.
 *
 * Three answers: put it on my oldest invoices, put it on these invoices, or
 * mail me a check. Nothing is applied from here — the request emails our team
 * and they do it in QuickBooks. Amounts are re-checked on the server against
 * the QuickBooks mirror; what this form shows is a convenience, not the rule.
 */

type Choice = 'oldest' | 'pick' | 'check';

const round2 = (n: number) => Math.round(n * 100) / 100;

function oldestFirst(credit: number, invoices: RequestOpenInvoice[]): Map<string, number> {
  const out = new Map<string, number>();
  let left = round2(credit);
  for (const inv of [...invoices].sort((a, b) => String(a.txnDate ?? '').localeCompare(String(b.txnDate ?? '')))) {
    if (left <= 0.005) break;
    const take = round2(Math.min(left, inv.balance));
    if (take > 0) out.set(inv.qboInvoiceId, take);
    left = round2(left - take);
  }
  return out;
}

export default function CreditRequestForm({
  credit,
  storeLabel,
  openInvoices,
  onCancel,
  onSent,
}: {
  credit: CreditRow;
  storeLabel: string | null;
  openInvoices: RequestOpenInvoice[];
  onCancel: () => void;
  onSent: (message: string) => void;
}) {
  const create = useCreateCreditRequest();
  const hasInvoices = openInvoices.length > 0;
  const [choice, setChoice] = useState<Choice>(hasInvoices ? 'oldest' : 'check');
  const suggested = useMemo(() => oldestFirst(credit.creditAmount, openInvoices), [credit.creditAmount, openInvoices]);
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  const [checkAmount, setCheckAmount] = useState(credit.creditAmount.toFixed(2));
  const [mailTo, setMailTo] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pickedTotal = round2([...picked.values()].reduce((s, v) => s + (Number(v) || 0), 0));
  const overCredit = pickedTotal > credit.creditAmount + 0.005;

  const toggleInvoice = (inv: RequestOpenInvoice) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(inv.qboInvoiceId)) {
        next.delete(inv.qboInvoiceId);
      } else {
        const used = round2([...next.values()].reduce((s, v) => s + (Number(v) || 0), 0));
        const left = Math.max(0, round2(credit.creditAmount - used));
        const amount = round2(Math.min(inv.balance, left > 0 ? left : inv.balance));
        next.set(inv.qboInvoiceId, amount.toFixed(2));
      }
      return next;
    });

  const submit = async () => {
    setError(null);
    try {
      if (choice === 'pick') {
        if (picked.size === 0) {
          setError('Tick at least one invoice, or choose "my oldest invoices".');
          return;
        }
        if (overCredit) {
          setError(`Those add up to ${formatUsd(pickedTotal)}, more than the ${formatUsd(credit.creditAmount)} available.`);
          return;
        }
      }
      const res = await create.mutateAsync({
        source_kind: credit.kind,
        source_id: credit.sourceId,
        request_type: choice === 'check' ? 'refund_check' : 'apply',
        apply_oldest: choice === 'oldest',
        invoices:
          choice === 'pick'
            ? [...picked.entries()].map(([qbo_invoice_id, amount]) => ({ qbo_invoice_id, amount: Number(amount) }))
            : undefined,
        amount: choice === 'check' ? Number(checkAmount) : undefined,
        mail_to: choice === 'check' ? mailTo || null : null,
        notes: notes || null,
      });
      onSent(
        res.emailed
          ? 'Thanks — your request is with our accounting team. We usually take care of it within 1–3 business days.'
          : 'Your request is saved and our team will see it. (Our notification email didn’t go out, so it may take a little longer — feel free to call.)',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send your request');
    }
  };

  const radio = (value: Choice, label: string, hint: string, disabled = false) => (
    <label
      className={`flex cursor-pointer gap-3 rounded-lg border px-3 py-2.5 ${
        choice === value ? 'border-brix-primary bg-brix-primary-tint/30' : 'border-brix-border'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <input
        type="radio"
        name={`choice-${credit.kind}-${credit.sourceId}`}
        className="mt-1"
        checked={choice === value}
        disabled={disabled}
        onChange={() => setChoice(value)}
      />
      <span>
        <span className="block text-sm font-semibold text-brix-ink">{label}</span>
        <span className="block text-xs text-brix-muted">{hint}</span>
      </span>
    </label>
  );

  return (
    <div className="border-t border-brix-border bg-brix-surface-alt/40 px-5 py-4 sm:pl-12">
      <p className="text-sm font-semibold text-brix-ink">
        What would you like done with {formatUsd(credit.creditAmount)}?
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {radio(
          'oldest',
          'Apply to my oldest invoices',
          hasInvoices ? 'We start with the oldest and work forward.' : 'You have no open invoices right now.',
          !hasInvoices,
        )}
        {radio(
          'pick',
          'Apply to invoices I choose',
          hasInvoices ? 'Tick the invoices and how much goes on each.' : 'You have no open invoices right now.',
          !hasInvoices,
        )}
        {radio('check', 'Send me a check', 'We mail you a check for the amount you choose.')}
      </div>

      {choice === 'oldest' && hasInvoices && (
        <p className="mt-3 text-xs text-brix-muted">
          At today&apos;s balances that would cover{' '}
          {[...suggested.keys()]
            .map((id) => openInvoices.find((i) => i.qboInvoiceId === id)?.docNumber ?? id)
            .join(', ')}
          .
        </p>
      )}

      {choice === 'pick' && (
        <div className="mt-3 max-h-72 overflow-auto rounded-lg border border-brix-border bg-white">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-brix-surface-alt text-left text-xs text-brix-muted">
              <tr>
                <th className="px-3 py-2" />
                <th className="px-3 py-2">Invoice</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-right">Apply</th>
              </tr>
            </thead>
            <tbody>
              {openInvoices.map((inv) => {
                const on = picked.has(inv.qboInvoiceId);
                return (
                  <tr key={inv.qboInvoiceId} className="border-t border-brix-border">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleInvoice(inv)}
                        aria-label={`Apply to invoice ${inv.docNumber ?? inv.qboInvoiceId}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-semibold">
                      {inv.docNumber ?? inv.qboInvoiceId}
                      {inv.storeName && !storeLabel ? (
                        <span className="ml-1 text-xs font-normal text-brix-muted">{inv.storeName}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-brix-muted">{inv.txnDate ? fmtMirrorDate(inv.txnDate) : ''}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{formatUsd(inv.balance)}</td>
                    <td className="px-3 py-2 text-right">
                      {on ? (
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          max={inv.balance}
                          className="w-24 rounded border border-brix-border px-2 py-1 text-right font-mono"
                          value={picked.get(inv.qboInvoiceId)}
                          onChange={(e) =>
                            setPicked((prev) => new Map(prev).set(inv.qboInvoiceId, e.target.value))
                          }
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div
            className={`sticky bottom-0 flex justify-between border-t border-brix-border bg-brix-surface-alt px-3 py-2 text-sm font-semibold ${
              overCredit ? 'text-red-700' : 'text-brix-ink'
            }`}
          >
            <span>Total to apply</span>
            <span className="font-mono tabular-nums">
              {formatUsd(pickedTotal)} of {formatUsd(credit.creditAmount)}
            </span>
          </div>
        </div>
      )}

      {choice === 'check' && (
        <div className="mt-3 grid gap-3 sm:grid-cols-[180px_1fr]">
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-brix-muted">Check amount</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={credit.creditAmount}
              className="w-full rounded border border-brix-border px-2 py-1.5 font-mono"
              value={checkAmount}
              onChange={(e) => setCheckAmount(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-brix-muted">
              Mail it to (leave blank to use the billing address we have)
            </span>
            <textarea
              rows={2}
              className="w-full rounded border border-brix-border px-2 py-1.5"
              value={mailTo}
              onChange={(e) => setMailTo(e.target.value)}
              placeholder="Name, street, city, state, ZIP"
            />
          </label>
        </div>
      )}

      <label className="mt-3 block text-sm">
        <span className="mb-1 block text-xs font-semibold text-brix-muted">Anything we should know? (optional)</span>
        <textarea
          rows={2}
          className="w-full rounded border border-brix-border px-2 py-1.5"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      {error && <p className="mt-3 text-sm font-semibold text-red-700">{error}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="rounded-lg bg-brix-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brix-primary/90 disabled:opacity-50"
          disabled={create.isPending}
          onClick={() => void submit()}
        >
          {create.isPending ? 'Sending…' : 'Send to our team'}
        </button>
        <button
          type="button"
          className="text-sm font-semibold text-brix-muted hover:text-brix-ink"
          onClick={onCancel}
          disabled={create.isPending}
        >
          Cancel
        </button>
        <span className="text-xs text-brix-muted">
          Nothing changes on your account until our team applies it.
        </span>
      </div>
    </div>
  );
}
